<template>
    <div class="index-container" :style="containerStyle">
        <div class="glass-overlay"></div>
        <a-layout class="layout-wrapper">
            <a-layout-header class="header-container">
                <div class="header-content">
                    <!-- 居中的 SiteClock 组件 -->
                    <SiteClock/>
                    
                    <!-- 设置按钮，绝对定位到右侧 -->
                    <div class="settings-btn-wrapper">
                        <a-tooltip content="系统设置" position="bottom">
                            <a-button
                                shape="circle"
                                type="text"
                                size="large"
                                class="settings-button"
                                @click="openSettings"
                            >
                                <template #icon>
                                    <icon-settings :style="{ fontSize: '24px', color: '#fff' }" />
                                </template>
                            </a-button>
                        </a-tooltip>
                    </div>
                </div>

                <!-- 引入设置弹入框组件 -->
                <SettingsDialog v-model:visible="isSettingsVisible"/>
            </a-layout-header>
            
            <a-layout-content class="main-content">
                <div class="content-limit">
                    <OrganizationList/>
                </div>
            </a-layout-content>
            
            <a-layout-footer class="footer-container">
                <div class="footer-text">© 2025 MingChen Navigation</div>
            </a-layout-footer>
        </a-layout>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { IconSettings } from '@arco-design/web-vue/es/icon';
import OrganizationList from '@/components/OrganizationList.vue';
import SiteClock from "@/components/layouts/SiteClock.vue";
import SettingsDialog from '@/components/layouts/SettingsDialog.vue';

const store = useStore();
const isSettingsVisible = ref(false);

const backgroundUrl = computed(() => store.getters['wallpapers/backgroundUrl']);

const containerStyle = computed(() => ({
    backgroundImage: `url(${backgroundUrl.value})`,
}));

const fetchOrganizations = () => store.dispatch('organizations/fetchOrganizations');
const fetchUserWallpapers = () => store.dispatch('wallpapers/fetchUserWallpapers');

const openSettings = () => {
    fetchOrganizations();
    isSettingsVisible.value = true;
};

onMounted(() => {
    fetchUserWallpapers();
});
</script>

<style scoped>
.index-container {
    min-height: 100vh;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

/* 玻璃遮罩层 - 移除模糊，改为极淡的通透层 */
.glass-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    /* 仅保留极淡的黑色遮罩，确保白色文字可见，但不模糊背景 */
    background: rgba(0, 0, 0, 0.05); 
    pointer-events: none;
    z-index: 0;
}

.layout-wrapper {
    position: relative;
    z-index: 1;
    background: transparent;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.header-container {
    background: transparent;
    padding: 30px 0;
    height: auto;
}

.header-content {
    max-width: 1400px;
    margin: 0 auto;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 20px;
}

.settings-btn-wrapper {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
}

.settings-button {
    transition: all 0.3s;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.2);
}

.settings-button:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg) scale(1.1);
    box-shadow: 0 0 15px rgba(255,255,255,0.3);
}

.main-content {
    flex: 1;
    padding: 20px;
    display: flex;
    justify-content: center;
    overflow-y: auto; /* 允许内容区域滚动 */
}

.content-limit {
    width: 100%;
    max-width: 1200px; /* 收窄到 1200px，让整体更像“上面那种” */
    padding-bottom: 40px;
}

.footer-container {
    text-align: center;
    padding: 20px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
    text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

/* 隐藏 Arco Layout 默认背景 */
:deep(.arco-layout),
:deep(.arco-layout-header),
:deep(.arco-layout-content),
:deep(.arco-layout-footer) {
    background-color: transparent;
}
</style>

<style>
/* 全局滚动条优化 */
::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

::-webkit-scrollbar-track {
    background: transparent; 
}

::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3); 
    border-radius: 4px;
    backdrop-filter: blur(4px);
}

::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5); 
}

html, body {
    margin: 0;
    padding: 0;
    height: 100%;
    -webkit-font-smoothing: antialiased;
}
</style>
